import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { getCensusUploadQueue } from '../api/census-enrollment-platform-api-client'
import {
  CensusUploadJobDto,
  CensusUploadQueueRequestDto,
} from '../api/dto/census-upload-job.dto'
import {
  CENSUS_QUEUE_POLL_INTERVAL_MS,
  getActiveUploadIds,
  hasActiveUploads,
  hasFinishedUploads,
} from '../utils/census-upload-job'
import { DRAFTS_QUEUE_FETCH_LIMIT } from '../utils/drafts-queue-rows'

export const DRAFTS_QUEUE_QUERY_KEY = 'censusDraftsQueue'

/**
 * Unfinished uploads pinned above Drafts. Polls only while one is queued or
 * processing, and calls onJobsFinished once per poll in which any finished.
 */
export function useDraftsQueueRows({
  filters,
  refreshKey,
  enabled,
  onJobsFinished,
}: {
  filters: CensusUploadQueueRequestDto
  refreshKey: boolean
  enabled: boolean
  onJobsFinished?: () => void
}): { jobs: CensusUploadJobDto[]; total: number; refetch: () => void } {
  const activeIdsRef = useRef<Set<string>>(new Set())
  const { data, refetch } = useQuery({
    queryKey: [DRAFTS_QUEUE_QUERY_KEY, filters, refreshKey],
    queryFn: () =>
      getCensusUploadQueue({
        ...filters,
        page: 1,
        limit: DRAFTS_QUEUE_FETCH_LIMIT,
      }),
    enabled,
    refetchInterval: (query) =>
      hasActiveUploads(query.state.data?.data ?? [])
        ? CENSUS_QUEUE_POLL_INTERVAL_MS
        : false,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!data) {
      return
    }
    const activeIds = getActiveUploadIds(data.data)
    const hasFinished = hasFinishedUploads(activeIdsRef.current, activeIds)
    activeIdsRef.current = activeIds
    if (hasFinished) {
      onJobsFinished?.()
    }
  }, [data, onJobsFinished])

  return {
    jobs: enabled ? (data?.data ?? []) : [],
    total: enabled ? (data?.meta.total ?? 0) : 0,
    refetch,
  }
}
